import React, { useState, useMemo } from 'react';
import { Search, X, Check } from 'lucide-react';

// Extensive Google Fonts library for the "More fonts" picker
export const EXTENDED_GOOGLE_FONTS = [
  { name: 'Amatic SC', category: 'Handwriting', family: "'Amatic SC', cursive" },
  { name: 'Arial', category: 'Sans Serif', family: "Arial, sans-serif" },
  { name: 'Abril Fatface', category: 'Display', family: "'Abril Fatface', cursive" },
  { name: 'Bebas Neue', category: 'Display', family: "'Bebas Neue', cursive" },
  { name: 'Cambo', category: 'Serif', family: "'Cambo', serif" },
  { name: 'Caveat', category: 'Handwriting', family: "'Caveat', cursive" },
  { name: 'Cinzel', category: 'Serif', family: "'Cinzel', serif" },
  { name: 'Comfortaa', category: 'Display', family: "'Comfortaa', cursive" },
  { name: 'Comic Sans MS', category: 'Handwriting', family: "'Comic Sans MS', cursive, sans-serif" },
  { name: 'Courier New', category: 'Monospace', family: "'Courier New', monospace" },
  { name: 'Dancing Script', category: 'Handwriting', family: "'Dancing Script', cursive" },
  { name: 'EB Garamond', category: 'Serif', family: "'EB Garamond', serif" },
  { name: 'Fira Code', category: 'Monospace', family: "'Fira Code', monospace" },
  { name: 'Georgia', category: 'Serif', family: "Georgia, serif" },
  { name: 'Impact', category: 'Display', family: "Impact, Charcoal, sans-serif" },
  { name: 'Inter', category: 'Sans Serif', family: "'Inter', sans-serif" },
  { name: 'Lato', category: 'Sans Serif', family: "'Lato', sans-serif" },
  { name: 'Lexend', category: 'Sans Serif', family: "'Lexend', sans-serif" },
  { name: 'Lobster', category: 'Display', family: "'Lobster', cursive" },
  { name: 'Lora', category: 'Serif', family: "'Lora', serif" },
  { name: 'Merriweather', category: 'Serif', family: "'Merriweather', serif" },
  { name: 'Montserrat', category: 'Sans Serif', family: "'Montserrat', sans-serif" },
  { name: 'Nunito', category: 'Sans Serif', family: "'Nunito', sans-serif" },
  { name: 'Open Sans', category: 'Sans Serif', family: "'Open Sans', sans-serif" },
  { name: 'Oswald', category: 'Sans Serif', family: "'Oswald', sans-serif" },
  { name: 'Pacifico', category: 'Handwriting', family: "'Pacifico', cursive" },
  { name: 'Permanent Marker', category: 'Handwriting', family: "'Permanent Marker', cursive" },
  { name: 'Playfair Display', category: 'Serif', family: "'Playfair Display', serif" },
  { name: 'Poppins', category: 'Sans Serif', family: "'Poppins', sans-serif" },
  { name: 'Quicksand', category: 'Sans Serif', family: "'Quicksand', sans-serif" },
  { name: 'Raleway', category: 'Sans Serif', family: "'Raleway', sans-serif" },
  { name: 'Roboto', category: 'Sans Serif', family: "'Roboto', sans-serif" },
  { name: 'Roboto Mono', category: 'Monospace', family: "'Roboto Mono', monospace" },
  { name: 'Roboto Serif', category: 'Serif', family: "'Roboto Serif', serif" },
  { name: 'Space Mono', category: 'Monospace', family: "'Space Mono', monospace" },
  { name: 'Spectral', category: 'Serif', family: "'Spectral', serif" },
  { name: 'Times New Roman', category: 'Serif', family: "'Times New Roman', serif" },
  { name: 'Trebuchet MS', category: 'Sans Serif', family: "'Trebuchet MS', sans-serif" },
  { name: 'Verdana', category: 'Sans Serif', family: "Verdana, sans-serif" },
];

const MoreFontsModal = ({
  isOpen,
  onClose,
  onSelectFont,
  activeFontName = 'Arial',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFont, setSelectedFont] = useState(activeFontName);

  const filteredFonts = useMemo(() => {
    return EXTENDED_GOOGLE_FONTS.filter((font) => {
      const matchesSearch = font.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCat =
        selectedCategory === 'All' ||
        font.category.toLowerCase() === selectedCategory.toLowerCase();
      return matchesSearch && matchesCat;
    });
  }, [searchTerm, selectedCategory]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const fontObj = EXTENDED_GOOGLE_FONTS.find((f) => f.name === selectedFont);
    if (fontObj && onSelectFont) {
      onSelectFont(fontObj);
    }
    onClose();
  };

  return (
    <div className="rte-gdocs-modal-overlay" onClick={onClose}>
      <div
        className="rte-gdocs-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Fonts dialog"
      >
        {/* Modal Header */}
        <div className="rte-gdocs-modal-header">
          <div className="rte-gdocs-modal-title">Fonts</div>
          <button
            type="button"
            className="rte-gdocs-modal-close-btn"
            onClick={onClose}
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="rte-gdocs-modal-toolbar">
          <div className="rte-gdocs-modal-search">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search fonts"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                className="rte-gdocs-search-clear"
                onClick={() => setSearchTerm('')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="rte-gdocs-modal-filter">
            <span className="rte-gdocs-filter-label">Show:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="rte-gdocs-modal-select"
            >
              <option value="All">All fonts</option>
              <option value="Sans Serif">Sans Serif</option>
              <option value="Serif">Serif</option>
              <option value="Display">Display</option>
              <option value="Handwriting">Handwriting</option>
              <option value="Monospace">Monospace</option>
            </select>
          </div>
        </div>

        {/* Font List Container */}
        <div className="rte-gdocs-modal-list">
          {filteredFonts.length === 0 ? (
            <div className="rte-gdocs-modal-empty">No fonts match your search.</div>
          ) : (
            filteredFonts.map((font) => {
              const isCurrent = selectedFont.toLowerCase() === font.name.toLowerCase();
              return (
                <div
                  key={font.name}
                  className={`rte-gdocs-modal-item ${isCurrent ? 'selected' : ''}`}
                  onClick={() => setSelectedFont(font.name)}
                >
                  <div className="rte-gdocs-modal-item-left">
                    <span
                      className="rte-gdocs-modal-item-name"
                      style={{ fontFamily: font.family }}
                    >
                      {font.name}
                    </span>
                    <span className="rte-gdocs-modal-item-cat">{font.category}</span>
                  </div>
                  {isCurrent && (
                    <div className="rte-gdocs-modal-item-check">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Modal Footer */}
        <div className="rte-gdocs-modal-footer">
          <button
            type="button"
            className="rte-gdocs-modal-btn rte-gdocs-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rte-gdocs-modal-btn rte-gdocs-btn-primary"
            onClick={handleConfirm}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoreFontsModal;
